import * as React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";
import {
    ActionIcon,
    Box,
    Button,
    Container,
    Group,
    Indicator,
    Menu,
    Paper,
    Text,
    useComputedColorScheme,
    useMantineColorScheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { openSpotlight } from "@mantine/spotlight";
import { BiBookmarks, BiCollection, BiHome, BiMenuAltRight, BiSearchAlt2 } from "react-icons/bi";
import { HiOutlineDocumentText } from "react-icons/hi";
import { MdLogin, MdOutlinePlaylistPlay } from "react-icons/md";
import { MdCheck, MdDarkMode, MdLightMode, MdOutlineBrightnessAuto } from "react-icons/md";
import styles from "./styles/Header.module.css";
import icon from "../assets/podcast.svg";
import { markSearchOpenMethod } from "@/core/searchAnalytics";
import { Urls } from "@/core/urls";
import { useStores } from "@/hooks/useStores";
import { ThemeToggle } from "./theme/ThemeToggle";

const themeOptions = [
    { value: "auto", label: "Auto", icon: <MdOutlineBrightnessAuto size={16} /> },
    { value: "light", label: "Ljust", icon: <MdLightMode size={16} /> },
    { value: "dark", label: "Mörkt", icon: <MdDarkMode size={16} /> },
] as const;

const PlaylistToggle = observer(() => {
    const { playlistStore } = useStores();
    return (
        <Indicator
            className={styles.playlistIndicator}
            color="accent"
            disabled={playlistStore.episodes.length === 0}
            inline
            label={playlistStore.episodes.length}
            offset={4}
            position="top-end"
            size={16}
            zIndex={1}>
            <ActionIcon
                aria-label="Visa spellista"
                className={styles.playlistToggle}
                color={playlistStore.isOpen ? "accent" : "brand"}
                radius="xl"
                size="lg"
                variant={playlistStore.isOpen ? "filled" : "light"}
                onClick={(event) => {
                    event.preventDefault();
                    playlistStore.toggleOpen();
                }}>
                <MdOutlinePlaylistPlay size={24} />
            </ActionIcon>
        </Indicator>
    );
});

function SearchTrigger({ compact }: { compact: boolean }) {
    const openSearch = (event: React.MouseEvent<HTMLElement>) => {
        event.preventDefault();
        markSearchOpenMethod("click");
        openSpotlight();
    };

    if (compact) {
        return (
            <ActionIcon
                aria-label="Öppna sök (⌘K)"
                className={styles.searchTriggerCompact}
                color="teal"
                radius="xl"
                size="lg"
                variant="light"
                onClick={openSearch}>
                <BiSearchAlt2 size={18} />
            </ActionIcon>
        );
    }

    return (
        <Button
            aria-label="Öppna sök (⌘K)"
            className={styles.searchTrigger}
            color="teal"
            leftSection={<BiSearchAlt2 size={16} />}
            px="md"
            radius="xl"
            size="sm"
            variant="light"
            onClick={openSearch}>
            Sök
        </Button>
    );
}

const MobileNavigationMenu = observer(({ onLogin }: { onLogin: () => void }) => {
    const navigate = useNavigate();
    const { appStore, authStore, playlistStore } = useStores();
    const { colorScheme, setColorScheme } = useMantineColorScheme();

    return (
        <Menu position="bottom-end" shadow="md" width={220} withinPortal>
            <Menu.Target>
                <ActionIcon
                    aria-label="Öppna meny"
                    className={styles.menuTrigger}
                    color="brand"
                    radius="xl"
                    size="lg"
                    variant="light">
                    <BiMenuAltRight size={22} />
                </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Label>Navigering</Menu.Label>
                <Menu.Item leftSection={<BiHome size={16} />} onClick={() => navigate("/")}>
                    Hem
                </Menu.Item>
                <Menu.Item leftSection={<HiOutlineDocumentText size={16} />} onClick={() => navigate("/kategori")}>
                    Kategori
                </Menu.Item>
                <Menu.Item leftSection={<BiCollection size={16} />} onClick={() => navigate(Urls.collections())}>
                    Samlingar
                </Menu.Item>
                <Menu.Item leftSection={<BiBookmarks size={16} />} onClick={() => navigate(Urls.focusAreas())}>
                    Fokus
                </Menu.Item>

                {(authStore.isSignedIn || appStore.signInEnabled) && (
                    <>
                        <Menu.Divider />
                        <Menu.Label>Konto</Menu.Label>
                        {authStore.isSignedIn ? (
                            <Menu.Item leftSection={<MdLogin size={16} />} onClick={() => navigate(Urls.account())}>
                                Mitt konto
                            </Menu.Item>
                        ) : (
                            <Menu.Item leftSection={<MdLogin size={16} />} onClick={onLogin}>
                                Logga in
                            </Menu.Item>
                        )}
                    </>
                )}

                <Menu.Divider />
                <Menu.Label>Verktyg</Menu.Label>
                <Menu.Item
                    disabled={playlistStore.episodes.length === 0}
                    leftSection={<MdOutlinePlaylistPlay size={16} />}
                    rightSection={
                        playlistStore.episodes.length > 0 ? (
                            <Text size="xs">{playlistStore.episodes.length}</Text>
                        ) : null
                    }
                    onClick={() => playlistStore.toggleOpen()}>
                    Spellista
                </Menu.Item>

                <Menu.Divider />
                <Menu.Label>Tema</Menu.Label>
                {themeOptions.map((option) => (
                    <Menu.Item
                        key={option.value}
                        leftSection={option.icon}
                        rightSection={colorScheme === option.value ? <MdCheck size={16} /> : null}
                        onClick={() => setColorScheme(option.value)}>
                        {option.label}
                    </Menu.Item>
                ))}
            </Menu.Dropdown>
        </Menu>
    );
});

export const Header = observer(() => {
    const navigate = useNavigate();
    const computedColorScheme = useComputedColorScheme("light", { getInitialValueInEffect: true });
    const isDark = computedColorScheme === "dark";
    const isCompact = useMediaQuery("(max-width: 62em)") ?? false;

    function handleHome(event: React.MouseEvent<HTMLAnchorElement>) {
        event.preventDefault();
        navigate("/");
    }
    const { appStore, authStore } = useStores();
    return (
        <Paper
            className={`${styles.header} ${isDark ? styles.headerDark : styles.headerLight}`}
            component="header"
            radius={0}
            shadow="sm"
            withBorder={false}>
            <Container className={styles.container} size="lg">
                <Box className={styles.brandWrap}>
                    <a
                        className={`${styles.brand} ${isDark ? styles.brandDark : styles.brandLight}`}
                        onClick={handleHome}
                        href="/">
                        <img className={styles.icon} src={icon} alt="HittaPodd ikon" />
                        <Box>
                            <Text className={styles.brandTitle}>HittaPodd</Text>
                        </Box>
                    </a>
                </Box>

                {!isCompact && (
                    <nav aria-label="Huvudmeny" className={styles.nav}>
                        <NavLink to="/kategori">Kategorier</NavLink>
                        <NavLink to="/samling">Samlingar</NavLink>
                        <NavLink to="/fokus">Fokus</NavLink>
                        <NavLink to="/topplista">Topplistan</NavLink>
                        <NavLink to="/poddar">Nya poddar</NavLink>
                    </nav>
                )}

                <Group className={styles.actions} gap="sm">
                    <Group className={styles.primaryActions} gap="xs" wrap="nowrap">
                        <SearchTrigger compact={isCompact} />

                        {!isCompact &&
                            (authStore.isSignedIn ? (
                                <Button
                                    color="accent"
                                    component={NavLink}
                                    miw={40}
                                    px="md"
                                    radius="xl"
                                    size="sm"
                                    to={Urls.account()}
                                    variant="filled"
                                    leftSection={<MdLogin />}>
                                    Mitt konto
                                </Button>
                            ) : (
                                appStore.signInEnabled && (
                                    <Button
                                        color="accent"
                                        miw={40}
                                        px="md"
                                        radius="xl"
                                        size="sm"
                                        variant="filled"
                                        leftSection={<MdLogin />}
                                        onClick={() => appStore.openSignIn("header")}>
                                        Logga in
                                    </Button>
                                )
                            ))}
                    </Group>

                    <Group className={styles.utilityActions} gap="xs" wrap="nowrap">
                        {isCompact ? (
                            <MobileNavigationMenu onLogin={() => appStore.openSignIn("header")} />
                        ) : (
                            <>
                                <ThemeToggle size="lg" />
                                <PlaylistToggle />
                            </>
                        )}
                    </Group>
                </Group>
            </Container>
        </Paper>
    );
});
